import { ErrorResponse } from '@apollo/client/link/error';
import get from 'lodash/get';
import { useSetRecoilState } from 'recoil';

import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

type AlertPageAlerts = {
  showUpdateSuccess: () => void;
  showDeleteSuccess: () => void;
  showMuteSuccess: () => void;
  showUnMuteSuccess: () => void;
  showCreateSuccess: () => void;
  showError: (error: ErrorResponse) => void;
};

/**
 * This hook is a separate concern for calling page alerts related to the alerts page.
 */

function useAlertPageAlerts(): AlertPageAlerts {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const showSuccess = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
    });
  };

  const showUpdateSuccess = (): void => {
    showSuccess('Alert has been updated');
  };

  const showDeleteSuccess = (): void => {
    showSuccess('Alert(s) have been deleted');
  };

  const showMuteSuccess = (): void => {
    showSuccess('Alert(s) have been muted');
  };

  const showUnMuteSuccess = (): void => {
    showSuccess('Alert(s) have been un-muted');
  };

  const showCreateSuccess = (): void => {
    showSuccess('Alert added');
  };

  /**
   * This handles the error when an alert is unsuccessfully mutated.
   *
   * @param error      The Error Response
   */
  const showError = (error: ErrorResponse): void => {
    const message = get(error.graphQLErrors, '[0].message', '');
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.WARNING,
      autoHide: true,
    });
  };

  return {
    showUpdateSuccess,
    showDeleteSuccess,
    showMuteSuccess,
    showUnMuteSuccess,
    showCreateSuccess,
    showError,
  };
}

export default useAlertPageAlerts;
